import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { type Dispatch, type UnknownAction } from "redux";
import * as Sentry from "@sentry/browser";

import {
  API_URL_ACCEPT_TERMS_AS_TRADER_REPRESENTATIVE,
  listTermsAndConditions as listTermsAndConditionsAction,
  userDetail,
} from "../../actions";
import client from "../../client";
import { TermsAndConditionsTarget } from "../../constants";
import { TermsOfUseDialog } from "../TermsOfUseDialog";
import { DispatchProps, RepresentativeTermsGateProps, ReduxState, StateProps } from "./types";

const TERMS_TARGET = TermsAndConditionsTarget.AGGREGATOR_ADMINS_FARMERS_TRADERS;

type AppDispatch = Dispatch<UnknownAction>;

// The thunk middleware lets us dispatch action creators that return functions,
// which redux's base Dispatch type disallows, hence the cast.
const dispatchAction = (dispatch: AppDispatch, action: unknown) =>
  dispatch(action as UnknownAction);

// Blocking gate shown on every representative route until the buyer has accepted
// the latest Terms of Use. Acceptance (incl. date & time) is persisted by the
// backend `user_accepts` endpoint.
export const RepresentativeTermsGate = ({
  currentUser,
  termsAndConditions,
  fetchUserDetails,
  fetchTermsAndConditions,
}: RepresentativeTermsGateProps) => {
  const [isBusy, setIsBusy] = useState(false);

  useEffect(() => {
    fetchUserDetails();
    fetchTermsAndConditions();
  }, [fetchUserDetails, fetchTermsAndConditions]);

  const needsAcceptance =
    !!termsAndConditions &&
    !!currentUser &&
    currentUser.terms_and_conditions_accepted_version !==
      currentUser.terms_and_conditions_latest_version;

  const onAccept = async () => {
    setIsBusy(true);
    try {
      await client.post(API_URL_ACCEPT_TERMS_AS_TRADER_REPRESENTATIVE);
      // Refetching the user detail updates the accepted version and closes the gate.
      fetchUserDetails();
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      // Always re-enable the button so a stale/failed refetch can't strand the
      // user on a permanently-disabled "waiting..." dialog.
      setIsBusy(false);
    }
  };

  if (!needsAcceptance) {
    return null;
  }

  return (
    <TermsOfUseDialog
      open
      isBusy={isBusy}
      markdown={termsAndConditions.long_text}
      onAccept={onAccept}
    />
  );
};

const mapStateToProps = (state: ReduxState): StateProps => ({
  currentUser: state.userDetail.data,
  termsAndConditions: state.termsAndConditions.data.results?.find(
    terms => terms.target === TERMS_TARGET
  ),
});

const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => ({
  fetchUserDetails: () => dispatchAction(dispatch, userDetail("", "", true)),
  fetchTermsAndConditions: () =>
    dispatchAction(dispatch, listTermsAndConditionsAction({ page_size: 9999, page: 1 })),
});

export default connect(mapStateToProps, mapDispatchToProps)(RepresentativeTermsGate);
